import {
  DisplayMenuItem,
  DisplayMenuSection,
  TypeDisplayMenuOptionGroupOptions,
  TypeDisplayMenuSections,
} from "models/menu/menuContentsReducer";
import { isDisplayMenuItem } from "models/menu/menu";
import cloneDeep from "lodash-es/cloneDeep";
import mergeWith from "lodash-es/mergeWith";

/**
 * Removes a specified number of UUIDs from the end of a path string
 * @param path String of UUIDs delimited by | (e.g. "sectionUUID|itemMainUUID")
 * @param count Number of UUIDs to remove from the end (default: 1)
 * @returns The path without the specified number of UUIDs or empty string if path is invalid
 */
export const removeLastUUIDFromPath = (
  path: string,
  count: number = 1,
): string | undefined => {
  if (count <= 0) {
    return path;
  }

  if (!path) {
    return undefined;
  }

  const uuids = path.split("|");

  // If there aren't enough UUIDs to remove, return empty string
  if (uuids.length < count) {
    return undefined;
  }

  // Remove the specified number of UUIDs from the end
  uuids.splice(uuids.length - count, count);
  return uuids.join("|");
};

/**
 * Extracts the last UUID from a path string
 * @param path String of UUIDs delimited by | (e.g. "sectionUUID|itemMainUUID")
 * @returns The last UUID in the path or empty string if path is invalid
 */
export const getLastUUIDFromPath = (path: string): string => {
  if (!path) {
    return "";
  }

  const uuids = path.split("|");
  return uuids[uuids.length - 1];
};

/**
 * Combines an array of UUIDs into a single path string
 * @param uuids Array of UUIDs to combine
 * @returns String of UUIDs delimited by | (e.g. "sectionUUID|itemMainUUID")
 */
export const combineUUIDs = (uuids: string[]): string => {
  return uuids.join("|");
};

/**
 * Gets an object from menuContents at the specified path
 * @param menuContents The menu contents object
 * @param path String of UUIDs delimited by | (e.g. "sectionUUID|itemMainUUID")
 * @returns The object at the specified path or undefined if not found
 */
export const getMenuOptionByPath = (
  menuContents: TypeDisplayMenuSections,
  path: string,
  // ): TypeDisplayMenuSections | TypeDisplayMenuOptionGroupOptions | undefined => {
): DisplayMenuItem | DisplayMenuSection | undefined => {
  if (!path) {
    return undefined;
  }

  // Split the path into individual UUIDs
  const keys = path.split("|");
  let current: TypeDisplayMenuOptionGroupOptions = menuContents;

  // Navigate through each key in the path
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    // If we've reached a non-object or null/undefined, we can't continue
    if (!current || typeof current !== "object") {
      return undefined;
    }

    // Move to the next level
    const next = current[key];

    // If we have more keys to process, and we've found a menu item or section,
    // update current to point to the appropriate sub-collection
    if (i < keys.length - 1 && next) {
      if (isDisplayMenuItem(next)) {
        current = next.side_option_groups || {};
      } else {
        // Assume it's a DisplayMenuSection
        current = next.options || {};
      }
    } else if (i === keys.length - 1 && next) {
      // If this is the last key, return the found object
      return next;
    }
  }

  return undefined;
};

/**
 * Modifies an object in menuContents at the specified path - either updates (merges) or replaces it
 * @param menuContents The menu contents object
 * @param path String of UUIDs delimited by | (e.g. "sectionUUID|itemMainUUID")
 * @param newData The data to update with or replace with
 * @param operation Whether to 'update' (merge) or 'replace' the object
 * @returns The modified menu contents
 */
export const modifyMenuOptionByPath = (
  menuContents: TypeDisplayMenuSections,
  path: string,
  newData: Partial<DisplayMenuItem> | Partial<DisplayMenuSection>,
  operation: "update" | "replace" = "update",
): TypeDisplayMenuSections => {
  // Create a deep copy to avoid mutating the original
  const result = cloneDeep(menuContents);

  if (!path) {
    return menuContents;
  }

  // Split the path into individual UUIDs
  const keys = path.split("|");

  let current: TypeDisplayMenuOptionGroupOptions = result;

  // Navigate through the path
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    // If this is the last key, update or replace and break
    if (i === keys.length - 1) {
      if (operation === "update") {
        // Arrays (e.g. `food_tag_overrides`) are replaced, not merged index by
        // index, so a shorter or empty list clears the old rows.
        current[key] = mergeWith(
          {},
          current[key],
          newData,
          (_: unknown, srcValue: unknown) =>
            Array.isArray(srcValue) ? srcValue : undefined,
        );
      } else {
        current[key] = newData as DisplayMenuItem | DisplayMenuSection;
      }
      break;
    }

    // If the key doesn't exist or is not an object, path is invalid
    if (!current[key] || typeof current[key] !== "object") {
      return menuContents;
    }

    // Move to the next object
    const next = current[key];

    // If we need to go deeper, determine whether to access side_option_groups or options
    if (i < keys.length - 1) {
      if (isDisplayMenuItem(next)) {
        // Initialize side_option_groups if it doesn't exist
        if (!next.side_option_groups) {
          next.side_option_groups = {};
        }
        current = next.side_option_groups;
      } else {
        // Assume it's a DisplayMenuSection
        if (!next.options) {
          next.options = {};
        }
        current = next.options;
      }
    }
  }

  return result;
};

/**
 * Deletes an object in menuContents at the specified path
 * @param menuContents The menu contents object
 * @param path String of UUIDs delimited by | (e.g. "sectionUUID|itemMainUUID")
 * @returns The updated menu contents
 */
export const deleteMenuOptionByPath = (
  menuContents: TypeDisplayMenuSections,
  path: string,
): TypeDisplayMenuSections => {
  if (!path) {
    return menuContents;
  }

  // Create a deep copy to avoid mutating the original
  const result = cloneDeep(menuContents);

  // Split the path into individual UUIDs
  const keys = path.split("|");

  let current: TypeDisplayMenuOptionGroupOptions = result;

  // Navigate through the path
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    // If this is the last key, delete the object
    if (i === keys.length - 1) {
      delete current[key];
      break;
    }

    // If the key doesn't exist or is not an object, path is invalid
    if (!current[key] || typeof current[key] !== "object") {
      return menuContents;
    }

    // Move to the next object
    const next = current[key];

    // If we need to go deeper, determine whether to access side_option_groups or options
    if (i < keys.length - 1) {
      if (isDisplayMenuItem(next)) {
        // Initialize side_option_groups if it doesn't exist
        if (!next.side_option_groups) {
          current = {};
        } else {
          current = next.side_option_groups;
        }
      } else if (!next.options) {
        // Initialize options if it doesn't exist
        current = {};
      } else {
        current = next.options;
      }
    }
  }

  return result;
};
